import { ActionIcon, Box, Button, CloseButton, Flex, Group, Stack, Text } from '@mantine/core';
import { IconCaretDown, IconCaretUp } from '@tabler/icons-react';
import { ReactNode } from 'react';
import { FieldValues, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormControlInput, TextInputControl } from '@/core/components';

export interface ChapterBookAuthorListControlProps<T extends FieldValues> extends FormControlInput<T> {
  withAsterisk?: boolean;

  label: string;

  description?: string;
  isDisabled?: boolean;

  renderMembership: (v: { membershipName: string }) => ReactNode;
}

const defaultAuthor = {
  authorName: '',
};

export function ChapterBookAuthorListControl<T extends FieldValues>(props: ChapterBookAuthorListControlProps<T>) {
  const { label, description, name, isSubmitting, isDisabled, renderMembership } = props;
  const { fields, append, remove, move } = useFieldArray({ name, keyName: 'uuid' });
  const { t } = useTranslation();

  return (
    <Box>
      <Flex justify="space-between">
        <Box>
          <Text
            fw="500"
            size="xl"
          >
            {label}
          </Text>
          {description && <Text c="dimmed">{description}</Text>}
        </Box>
        <Group>
          <Button
            size="sm"
            disabled={isDisabled}
            // @ts-expect-error fix in the future
            onClick={() => append(defaultAuthor)}
          >
            {t('button.add')}
          </Button>
        </Group>
      </Flex>
      <Stack gap="xl">
        {fields.length === 0 && (
          <Text
            size="xs"
            c="dimmed"
            fs="italic"
          >
            {t('authors.emptyList')}
          </Text>
        )}
        {fields.map((field, index) => (
          <Flex
            align="flex-end"
            justify="center"
            gap="4px"
            key={field.uuid}
          >
            <Text
              mr="8px"
              mt="25px"
              size="sm"
            >
              #{index + 1}
            </Text>
            <Stack
              mt="12px"
              mr="8px"
              gap="2px"
            >
              <ActionIcon
                size={22}
                variant="outline"
                aria-label="sus"
                disabled={index === 0}
              >
                <IconCaretUp
                  onClick={() => move(index, Math.max(index - 1, 0))}
                  style={{ width: '70%', height: '70%' }}
                  stroke={1.5}
                />
              </ActionIcon>
              <ActionIcon
                size={22}
                variant="outline"
                aria-label="jos"
                disabled={index === fields.length - 1}
              >
                <IconCaretDown
                  onClick={() => move(index, Math.min(index + 1, fields.length - 1))}
                  style={{ width: '70%', height: '70%' }}
                  stroke={1.5}
                />
              </ActionIcon>
            </Stack>
            <TextInputControl
              label={t('authors.form.fields.userName')}
              name={`${name}.${index}.authorName`}
              isSubmitting={isSubmitting}
              isDisabled={isDisabled}
            />
            {renderMembership({ membershipName: `${name}.${index}.membership` })}
            <CloseButton
              mt="25px"
              onClick={() => remove(index)}
              c="red"
            />
          </Flex>
        ))}
      </Stack>
    </Box>
  );
}
